// src/pages/VendorRegistrationPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBuilding, FaArrowLeft, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import PublicLayout from '../components/public/PublicLayout';

const VENDOR_TIERS = [
    {
        tier: 'Bronze', price: 150,
        benefits: [
            'Certificate of partnership recognition',
            'Access to ARCA newsletters and communication channels',
            'ARCA Bronze Industry Partner Badge and Certificate of Membership'
        ]
    },
    {
        tier: 'Silver', price: 200,
        benefits: [
            'Digital advertisement on ARCA Website (12 months)',
            'Quarterly social media acknowledgment and spotlight features',
            'Access to vendor community updates and market insights',
            'ARCA Silver Industry Partner Badge and Certificate of Membership'
        ]
    },
    {
        tier: 'Gold', price: 250,
        benefits: [
            'Advertise on ARCA Website',
            'Quarterly social media acknowledgment and spotlight features',
            'Access to ARCA newsletters and communication channels',
            'ARCA Gold Industry Partner Badge and Certificate of Membership'
        ]
    },
    {
        tier: 'Platinum', price: 300,
        benefits: [
            'Digital advertisement on ARCA Website (12 months)',
            'Direct access to ARCA leadership for strategic collaboration',
            'Featured placement in all ARCA newsletters and social media campaigns',
            'Promotion on ARCA Educational/Outreach Programs',
            'Quarterly social media acknowledgment and spotlight features',
            'ARCA Platinum Industry Partner Badge and Certificate of Membership'
        ]
    },
    {
        tier: 'Diamond', price: 500,
        benefits: [
            'Direct access to ARCA leadership for strategic collaboration',
            'Featured placement in all ARCA newsletters and social media campaigns',
            'Promotion on ARCA Educational/Outreach Programs',
            'Quarterly social media acknowledgment and spotlight features',
            'Top logo placement on the ARCA website\'s vendor directory with hyperlink',
            'Exclusive Event Access & Speaking Opportunities',
            'Enhanced Recognition, Diamond Badge and Certificate of Membership'
        ]
    }
];

const AREAS = [
    'Medical Equipment & Devices', 'Respiratory Care Equipment', 'Ventilators & CPAP Machines',
    'Personal Protective Equipment (PPE)', 'Diagnostic Equipment', 'Oxygen Therapy Equipment',
    'Nebulizers & Inhalers', 'Patient Monitoring Systems', 'Software & Technology Solutions',
    'Training & Education Services', 'Laboratory Equipment', 'Hospital Furniture & Supplies',
    'Pharmaceutical Products', 'Rehabilitation Equipment', 'Telehealth Solutions', 'Other'
];

const TIER_COLORS = {
    Bronze: { bg: '#cd7f32', light: '#fdf0e0', border: '#cd7f32' },
    Silver: { bg: '#9e9e9e', light: '#f5f5f5', border: '#9e9e9e' },
    Gold: { bg: '#ffd700', light: '#fffde7', border: '#ffd700' },
    Platinum: { bg: '#6B8E23', light: '#f0f4e8', border: '#6B8E23' },
    Diamond: { bg: '#0d6efd', light: '#e8f0fe', border: '#0d6efd' }
};

const getInitialState = () => ({
    // Tier
    vendorTier: 'Bronze',
    vendorPrice: 150,
    // Personal
    title: '', firstName: '', lastName: '', email: '', phone: '',
    streetAddress: '', city: '', stateProvince: '', postalCode: '', country: '',
    // Company
    companyName: '', companyAddress: '', companyDescription: '',
    areasOfConcentration: [], otherAreasOfConcentration: '',
    companyWebsite: '', companySize: '', partnershipGoals: '',
    // Files
    logo: null, document: null
});

const VendorRegistrationPage = ({ user, onLogout }) => {
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState(getInitialState());
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [reviewConfirmed, setReviewConfirmed] = useState(false);

    const steps = ['Select Tier', 'Contact Details', 'Company Details', 'Areas & Goals', 'Review & Submit'];

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleAreaToggle = (area) => {
        setFormData(prev => {
            const arr = prev.areasOfConcentration;
            return {
                ...prev,
                areasOfConcentration: arr.includes(area) ? arr.filter(a => a !== area) : [...arr, area]
            };
        });
    };

    const handleFileChange = (field, file) => {
        setFormData(prev => ({ ...prev, [field]: file }));
        if (field === 'logo' && file) {
            const reader = new FileReader();
            reader.onload = (e) => setLogoPreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const validate = (stepIndex) => {
        const e = {};
        const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (stepIndex === 0) {
            if (!formData.vendorTier) e.vendorTier = 'Please select a tier.';
        }
        if (stepIndex === 1) {
            if (!formData.firstName) e.firstName = 'First name is required.';
            if (!formData.lastName) e.lastName = 'Last name is required.';
            if (!formData.email) e.email = 'Email is required.';
            else if (!email.test(formData.email)) e.email = 'Enter a valid email address.';
            if (!formData.phone) e.phone = 'Phone number is required.';
            if (!formData.country) e.country = 'Country is required.';
        }
        if (stepIndex === 2) {
            if (!formData.companyName) e.companyName = 'Company name is required.';
            if (!formData.companyDescription) e.companyDescription = 'Company description is required.';
        }
        if (stepIndex === 3) {
            if (!formData.areasOfConcentration || formData.areasOfConcentration.length === 0) {
                e.areasOfConcentration = 'Please select at least one area of concentration.';
            }
        }
        return e;
    };

    const nextStep = () => {
        const errs = validate(step);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            toast.error('Please correct the errors before proceeding.');
            return;
        }
        setErrors({});
        setStep(prev => prev + 1);
    };

    const prevStep = () => setStep(prev => prev - 1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const finalErrors = validate(step);
        if (Object.keys(finalErrors).length > 0) {
            setErrors(finalErrors);
            toast.error('Please correct the errors.');
            return;
        }

        setIsSubmitting(true);
        toast.info('Submitting your vendor application...');

        try {
            const data = new FormData();

            // Map fields to backend schema
            data.append('company_name', formData.companyName);
            data.append('contact_person', `${formData.firstName} ${formData.lastName}`.trim());
            data.append('contact_person_title', formData.title || '');
            data.append('email', formData.email);
            data.append('phone', formData.phone);
            data.append('country', formData.country);
            data.append('company_address', formData.companyAddress || '');
            data.append('street_address', formData.streetAddress || '');
            data.append('city', formData.city || '');
            data.append('state_province', formData.stateProvince || '');
            data.append('postal_code', formData.postalCode || '');
            data.append('category', formData.areasOfConcentration[0] || '');
            data.append('categories', JSON.stringify(formData.areasOfConcentration));
            data.append('other_category', formData.otherAreasOfConcentration || '');
            data.append('website', formData.companyWebsite || '');
            data.append('description', formData.companyDescription);
            data.append('company_size', formData.companySize || '');
            data.append('partnership_goals', formData.partnershipGoals || '');
            data.append('vendor_tier', formData.vendorTier);
            data.append('vendor_price', formData.vendorPrice);
            data.append('title', formData.title || '');
            data.append('first_name', formData.firstName);
            data.append('last_name', formData.lastName);

            if (formData.logo) data.append('logo', formData.logo);
            if (formData.document) data.append('document', formData.document);

            const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');
            const response = await fetch(`${API_BASE}/api/vendors/register`, { method: 'POST', body: data });
            const result = await response.json();

            if (!response.ok) throw new Error(result.error || result.message || 'Registration failed');

            setSubmitted(true);
            toast.success('Vendor application submitted successfully!');
        } catch (error) {
            console.error('Vendor registration error:', error);
            toast.error(`Submission failed: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <PublicLayout user={user} onLogout={onLogout}>
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 text-center">
                            <div className="card border-0 shadow-lg p-5">
                                <FaCheckCircle size={80} style={{ color: '#6B8E23' }} className="mx-auto mb-4" />
                                <h2 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>Application Submitted!</h2>
                                <p className="text-muted mb-4">
                                    Thank you for your vendor application. Our admin team will review your submission
                                    and get back to you within 2-5 business days.
                                </p>
                                <div className="alert" style={{ backgroundColor: '#f0f4e8', borderLeft: '4px solid #6B8E23' }}>
                                    <strong>What happens next?</strong>
                                    <ul className="text-start mt-2 mb-0">
                                        <li>Admin reviews your application</li>
                                        <li>You'll receive an email with the decision</li>
                                        <li>Upon approval, your company appears in the vendor directory</li>
                                    </ul>
                                </div>
                                <Link to="/" className="btn btn-lg text-white mt-3" style={{ backgroundColor: '#6B8E23' }}>
                                    Return to Home
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    const selectedTier = VENDOR_TIERS.find(t => t.tier === formData.vendorTier);

    return (
        <PublicLayout user={user} onLogout={onLogout}>
            <div className="py-4 py-lg-5" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e8f5e9 100%)', minHeight: '80vh' }}>
                <div className="container" style={{ maxWidth: '860px' }}>
                    {/* Header */}
                    <div className="text-center mb-4">
                        <FaBuilding size={48} style={{ color: '#6B8E23' }} className="mb-3" />
                        <h1 className="display-5 fw-bold mb-2">
                            <span style={{ color: '#6B8E23' }}>Vendor</span> Membership Registration
                        </h1>
                        <p className="text-muted">Join ARCA as an Industry Partner and expand your reach across Africa</p>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="small fw-semibold" style={{ color: '#6B8E23' }}>{steps[step]}</span>
                            <span className="small text-muted">Step {step + 1} of {steps.length}</span>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                            <div
                                className="progress-bar"
                                style={{ backgroundColor: '#6B8E23', width: `${((step + 1) / steps.length) * 100}%`, transition: 'width 0.4s ease' }}
                            />
                        </div>
                        {/* Step indicators */}
                        <div className="d-flex justify-content-between mt-2">
                            {steps.map((s, i) => (
                                <span key={i} className="small" style={{ color: i <= step ? '#6B8E23' : '#adb5bd', fontWeight: i === step ? 600 : 400, fontSize: '0.7rem' }}>
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="card border-0 shadow-lg">
                            <div className="card-body p-4 p-lg-5">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={step}
                                        initial={{ opacity: 0, x: 40 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -40 }}
                                        transition={{ duration: 0.25 }}
                                    >

                                        {/* ── STEP 0: Tier Selection ── */}
                                        {step === 0 && (
                                            <div>
                                                <h3 className="h4 fw-bold mb-1" style={{ color: '#6B8E23' }}>Select Your Membership Tier</h3>
                                                <p className="text-muted mb-4">Choose the partnership level that best fits your organization</p>
                                                <div className="row g-3">
                                                    {VENDOR_TIERS.map(({ tier, price, benefits }) => {
                                                        const colors = TIER_COLORS[tier];
                                                        const selected = formData.vendorTier === tier;
                                                        return (
                                                            <div key={tier} className="col-12 col-md-6">
                                                                <div
                                                                    className="card h-100 cursor-pointer"
                                                                    style={{
                                                                        border: selected ? `3px solid ${colors.bg}` : '2px solid #dee2e6',
                                                                        backgroundColor: selected ? colors.light : '#fff',
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                    onClick={() => {
                                                                        handleChange('vendorTier', tier);
                                                                        handleChange('vendorPrice', price);
                                                                    }}
                                                                >
                                                                    <div className="card-body p-3">
                                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                                            <div className="d-flex align-items-center">
                                                                                <input type="radio" checked={selected} readOnly className="me-2" style={{ accentColor: colors.bg }} />
                                                                                <span className="fw-bold" style={{ color: colors.bg }}>{tier}</span>
                                                                            </div>
                                                                            <span className="fw-bold fs-5" style={{ color: colors.bg }}>${price}<small className="text-muted fs-6">/yr</small></span>
                                                                        </div>
                                                                        <ul className="mb-0 ps-3" style={{ fontSize: '0.8rem' }}>
                                                                            {benefits.slice(0, 3).map((b, i) => (
                                                                                <li key={i} className="mb-1 text-muted">{b}</li>
                                                                            ))}
                                                                            {benefits.length > 3 && <li className="text-muted">+{benefits.length - 3} more benefits</li>}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {selectedTier && (
                                                    <div className="alert mt-4" style={{ backgroundColor: TIER_COLORS[formData.vendorTier].light, borderLeft: `4px solid ${TIER_COLORS[formData.vendorTier].bg}` }}>
                                                        <h6 className="fw-bold mb-2" style={{ color: TIER_COLORS[formData.vendorTier].bg }}>
                                                            All {formData.vendorTier} Tier Benefits — ${formData.vendorPrice}/year
                                                        </h6>
                                                        <ul className="mb-0 ps-3">
                                                            {selectedTier.benefits.map((b, i) => (
                                                                <li key={i} className="mb-1" style={{ fontSize: '0.875rem' }}>
                                                                    <i className="fas fa-check-circle me-2" style={{ color: '#6B8E23' }}></i>{b}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {errors.vendorTier && <div className="text-danger small mt-2">{errors.vendorTier}</div>}
                                            </div>
                                        )}

                                        {/* ── STEP 1: Contact Details ── */}
                                        {step === 1 && (
                                            <div>
                                                <h3 className="h4 fw-bold mb-1" style={{ color: '#6B8E23' }}>Your Contact Details</h3>
                                                <p className="text-muted mb-4">Personal details of the primary contact person</p>
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-medium">Title</label>
                                                        <select className="form-select" value={formData.title} onChange={e => handleChange('title', e.target.value)}>
                                                            <option value="">Select...</option>
                                                            <option value="Mr.">Mr.</option>
                                                            <option value="Mrs.">Mrs.</option>
                                                            <option value="Ms.">Ms.</option>
                                                            <option value="Dr.">Dr.</option>
                                                            <option value="Prof.">Prof.</option>
                                                            <option value="RRT.">RRT.</option>
                                                            <option value="RCP.">RCP.</option>
                                                            <option value="RT.">RT.</option>
                                                            <option value="RN.">RN.</option>
                                                            <option value="CRNA">CRNA</option>
                                                            <option value="PT/DPT">PT/DPT</option>
                                                            <option value="OT/OTD">OT/OTD</option>
                                                            <option value="SLP">SLP</option>
                                                            <option value="PA/PA-C">PA/PA-C</option>
                                                            <option value="NP/APRN">NP/APRN</option>
                                                            <option value="DDS/DMD">DDS/DMD</option>
                                                            <option value="DVM">DVM</option>
                                                            <option value="PharmD">PharmD</option>
                                                            <option value="OD">OD</option>
                                                            <option value="LCSW">LCSW</option>
                                                            <option value="LPN">LPN</option>
                                                            <option value="LMT">LMT</option>
                                                            <option value="RD/RDN">RD/RDN</option>
                                                            <option value="DO">DO</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-medium">First Name <span className="text-danger">*</span></label>
                                                        <input className={`form-control ${errors.firstName ? 'is-invalid' : ''}`} value={formData.firstName} onChange={e => handleChange('firstName', e.target.value)} placeholder="John" />
                                                        {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-medium">Last Name <span className="text-danger">*</span></label>
                                                        <input className={`form-control ${errors.lastName ? 'is-invalid' : ''}`} value={formData.lastName} onChange={e => handleChange('lastName', e.target.value)} placeholder="Smith" />
                                                        {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium">Email <span className="text-danger">*</span></label>
                                                        <input type="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="john@company.com" />
                                                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium">Phone <span className="text-danger">*</span></label>
                                                        <input className={`form-control ${errors.phone ? 'is-invalid' : ''}`} value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+27 123 456 7890" />
                                                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label fw-medium">Street Address</label>
                                                        <input className="form-control" value={formData.streetAddress} onChange={e => handleChange('streetAddress', e.target.value)} placeholder="123 Business Ave" />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-medium">City</label>
                                                        <input className="form-control" value={formData.city} onChange={e => handleChange('city', e.target.value)} placeholder="Accra" />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-medium">State / Province</label>
                                                        <input className="form-control" value={formData.stateProvince} onChange={e => handleChange('stateProvince', e.target.value)} placeholder="Greater Accra" />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-medium">Postal Code</label>
                                                        <input className="form-control" value={formData.postalCode} onChange={e => handleChange('postalCode', e.target.value)} placeholder="00233" />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label fw-medium">Country <span className="text-danger">*</span></label>
                                                        <input className={`form-control ${errors.country ? 'is-invalid' : ''}`} value={formData.country} onChange={e => handleChange('country', e.target.value)} placeholder="Ghana" />
                                                        {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── STEP 2: Company Details ── */}
                                        {step === 2 && (
                                            <div>
                                                <h3 className="h4 fw-bold mb-1" style={{ color: '#6B8E23' }}>Company Details</h3>
                                                <p className="text-muted mb-4">Tell us about your organization</p>
                                                <div className="row g-3">
                                                    <div className="col-12">
                                                        <label className="form-label fw-medium">Company / Organization Name <span className="text-danger">*</span></label>
                                                        <input className={`form-control ${errors.companyName ? 'is-invalid' : ''}`} value={formData.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="MedTech Solutions Africa Ltd" />
                                                        {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label fw-medium">Company Address</label>
                                                        <textarea className="form-control" rows={2} value={formData.companyAddress} onChange={e => handleChange('companyAddress', e.target.value)} placeholder="123 Medical Park Drive, Sandton..." />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium">Company Website</label>
                                                        <input type="url" className="form-control" value={formData.companyWebsite} onChange={e => handleChange('companyWebsite', e.target.value)} placeholder="https://www.company.com" />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium">Company Size</label>
                                                        <select className="form-select" value={formData.companySize} onChange={e => handleChange('companySize', e.target.value)}>
                                                            <option value="">Select size</option>
                                                            <option value="1-10">1–10 employees</option>
                                                            <option value="11-50">11–50 employees</option>
                                                            <option value="51-200">51–200 employees</option>
                                                            <option value="201-1000">201–1000 employees</option>
                                                            <option value="1000+">1000+ employees</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label fw-medium">Company Description <span className="text-danger">*</span></label>
                                                        <textarea className={`form-control ${errors.companyDescription ? 'is-invalid' : ''}`} rows={4} value={formData.companyDescription} onChange={e => handleChange('companyDescription', e.target.value)} placeholder="Brief description of your company, products/services, and experience in healthcare..." />
                                                        {errors.companyDescription && <div className="invalid-feedback">{errors.companyDescription}</div>}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium">Company Logo</label>
                                                        {logoPreview && (
                                                            <div className="mb-2">
                                                                <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '4px', padding: '4px' }} />
                                                            </div>
                                                        )}
                                                        <input type="file" className="form-control" accept="image/*" onChange={e => handleFileChange('logo', e.target.files[0])} />
                                                        <small className="text-muted">PNG, JPG, GIF (max 5MB)</small>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label fw-medium">Business License / Verification Doc</label>
                                                        <input type="file" className="form-control" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => handleFileChange('document', e.target.files[0])} />
                                                        <small className="text-muted">PDF, DOC, or image (max 10MB)</small>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── STEP 3: Areas & Goals ── */}
                                        {step === 3 && (
                                            <div>
                                                <h3 className="h4 fw-bold mb-1" style={{ color: '#6B8E23' }}>Areas of Concentration</h3>
                                                <p className="text-muted mb-4">Select all areas your company specializes in <span className="text-danger">*</span></p>
                                                <div className="row g-2 mb-3">
                                                    {AREAS.map(area => (
                                                        <div key={area} className="col-md-6 col-lg-4">
                                                            <div
                                                                className="form-check p-3 rounded"
                                                                style={{
                                                                    backgroundColor: formData.areasOfConcentration.includes(area) ? '#f0f4e8' : '#f8f9fa',
                                                                    border: `2px solid ${formData.areasOfConcentration.includes(area) ? '#6B8E23' : '#dee2e6'}`,
                                                                    cursor: 'pointer', transition: 'all 0.15s'
                                                                }}
                                                                onClick={() => handleAreaToggle(area)}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input me-2"
                                                                    checked={formData.areasOfConcentration.includes(area)}
                                                                    onChange={() => handleAreaToggle(area)}
                                                                    style={{ accentColor: '#6B8E23' }}
                                                                />
                                                                <label className="form-check-label fw-medium" style={{ cursor: 'pointer', fontSize: '0.875rem' }}>{area}</label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {errors.areasOfConcentration && <div className="text-danger small">{errors.areasOfConcentration}</div>}
                                                {formData.areasOfConcentration.includes('Other') && (
                                                    <div className="mt-3">
                                                        <label className="form-label fw-medium">Please specify:</label>
                                                        <input className="form-control" value={formData.otherAreasOfConcentration} onChange={e => handleChange('otherAreasOfConcentration', e.target.value)} placeholder="Specify your area..." />
                                                    </div>
                                                )}
                                                <div className="mt-4">
                                                    <label className="form-label fw-medium">Partnership Goals <span className="text-muted fw-normal">(Optional)</span></label>
                                                    <textarea className="form-control" rows={4} value={formData.partnershipGoals} onChange={e => handleChange('partnershipGoals', e.target.value)} placeholder="Describe what you hope to achieve through partnership with ARCA..." />
                                                </div>
                                            </div>
                                        )}

                                        {/* ── STEP 4: Review & Submit ── */}
                                        {step === 4 && (
                                            <div>
                                                <h3 className="h4 fw-bold mb-4" style={{ color: '#6B8E23' }}>Review Your Application</h3>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div className="card border-0 bg-light p-3">
                                                            <h6 className="fw-bold mb-2" style={{ color: '#6B8E23' }}>Selected Tier</h6>
                                                            <p className="mb-0 fw-bold fs-5">{formData.vendorTier} — <span style={{ color: '#6B8E23' }}>${formData.vendorPrice}/year</span></p>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="card border-0 bg-light p-3">
                                                            <h6 className="fw-bold mb-2" style={{ color: '#6B8E23' }}>Contact</h6>
                                                            <p className="mb-1 small">{formData.title} {formData.firstName} {formData.lastName}</p>
                                                            <p className="mb-1 small">{formData.email}</p>
                                                            <p className="mb-0 small">{formData.phone} — {formData.country}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="card border-0 bg-light p-3">
                                                            <h6 className="fw-bold mb-2" style={{ color: '#6B8E23' }}>Company</h6>
                                                            <p className="mb-1 fw-bold">{formData.companyName}</p>
                                                            {formData.companyWebsite && <p className="mb-0 small"><a href={formData.companyWebsite} target="_blank" rel="noopener noreferrer">{formData.companyWebsite}</a></p>}
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="card border-0 bg-light p-3">
                                                            <h6 className="fw-bold mb-2" style={{ color: '#6B8E23' }}>Areas of Concentration</h6>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {formData.areasOfConcentration.map(a => (
                                                                    <span key={a} className="badge" style={{ backgroundColor: '#6B8E23' }}>{a}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {(formData.logo || formData.document) && (
                                                        <div className="col-12">
                                                            <div className="card border-0 bg-light p-3">
                                                                <h6 className="fw-bold mb-2" style={{ color: '#6B8E23' }}>Uploaded Files</h6>
                                                                {formData.logo && <p className="mb-1 small">Logo: {formData.logo.name}</p>}
                                                                {formData.document && <p className="mb-0 small">Document: {formData.document.name}</p>}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="alert alert-info mt-4">
                                                    <i className="bi bi-info-circle me-2"></i>
                                                    After submission, an ARCA admin will review your application and contact you within 2–5 business days.
                                                </div>
                                                <div className="form-check mt-3 p-3 rounded" style={{ backgroundColor: '#f0f4e8', border: '2px solid #6B8E23' }}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="reviewConfirm"
                                                        checked={reviewConfirmed}
                                                        onChange={e => setReviewConfirmed(e.target.checked)}
                                                        style={{ accentColor: '#6B8E23' }}
                                                    />
                                                    <label className="form-check-label fw-semibold ms-2" htmlFor="reviewConfirm" style={{ cursor: 'pointer' }}>
                                                        I have reviewed my application and confirm that all information provided is accurate.
                                                    </label>
                                                </div>
                                            </div>
                                        )}

                                    </motion.div>
                                </AnimatePresence>

                                {/* Navigation buttons */}
                                <div className="d-flex justify-content-between mt-4 pt-4 border-top">
                                    {step > 0 ? (
                                        <button type="button" onClick={prevStep} className="btn btn-outline-secondary d-flex align-items-center">
                                            <FaArrowLeft className="me-2" /> Previous
                                        </button>
                                    ) : (
                                        <Link to="/" className="btn btn-outline-secondary">
                                            <FaArrowLeft className="me-2" /> Back to Home
                                        </Link>
                                    )}

                                    {step < steps.length - 1 ? (
                                        <button type="button" onClick={nextStep} className="btn text-white d-flex align-items-center" style={{ backgroundColor: '#6B8E23' }}>
                                            Next <FaArrowRight className="ms-2" />
                                        </button>
                                    ) : (
                                        <button type="submit" className="btn btn-success d-flex align-items-center" disabled={isSubmitting || !reviewConfirmed}>
                                            {isSubmitting ? (
                                                <><span className="spinner-border spinner-border-sm me-2" />Submitting...</>
                                            ) : (
                                                <><FaCheckCircle className="me-2" />Submit Application</>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </PublicLayout>
    );
};

export default VendorRegistrationPage;
